import {
  Badge,
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Icon,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { citesFoaProvisions } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { FiChevronRight, FiClock, FiUsers } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  ParticipationRequestStatus,
  type BuyerParticipationRequestDto,
  type SourcingEventV2Dto,
  useListParticipationRequestsForBuyerQuery,
} from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { participationStatusBadge } from "../../../../common/qualification/participationStatusBadge";
import { urls } from "../../../../urls";
import { getRequestQualificationProgress } from "./detail/requestQualification";
import { deriveQualificationSteps } from "./deriveQualificationSteps";
import { InvitationOutcomeRecord } from "./InvitationOutcomeRecord";
import { QualificationStepper } from "./QualificationStepper";
import { SelectionPanel, useCandidateSelection } from "./SelectionPanel";
import { SendInvitationCard } from "./SendInvitationCard";
import { REVEAL_ANCHORS } from "../revealAnchor";

interface Props {
  event: SourcingEventV2Dto;
}

const SECTION_REVIEW = REVEAL_ANCHORS.qualificationReview;
const SECTION_SELECT = REVEAL_ANCHORS.invitationSelection;
const SECTION_SEND = "qualification-section-send";

export const RequestQualificationTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const { data, isLoading } = useListParticipationRequestsForBuyerQuery({ eventId: event.id });

  const qualificationPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const citesFoa = citesFoaProvisions(qualificationPublicCtx?.regulation);
  const limits = qualificationPublicCtx?.candidateLimits ?? null;
  const capped = limits != null;

  const requests = useMemo(() => data?.requests ?? [], [data]);
  const qualified = useMemo(
    () => requests.filter((r) => r.status === ParticipationRequestStatus.Qualified),
    [requests],
  );
  const unresolved = useMemo(
    () => requests.filter((r) => r.status === ParticipationRequestStatus.Submitted),
    [requests],
  );

  const selection = useCandidateSelection(event.id, qualified, limits);

  if (isLoading || !data) return null;

  // Pre-deadline: identities withheld
  if (!data.identitiesVisible) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10">
          <Flex direction="column" align="center" gap="3" textAlign="center">
            <Icon as={FiClock} boxSize="8" color="smPrimary" />
            <Text fontWeight="semibold" color="smText">
              {t("The request phase is open")}
            </Text>
            {data.requestDeadline && (
              <Text fontSize="sm" color="smMuted">
                {t("Deadline for requests to participate")}:{" "}
                <b>{displayDateWithTime(data.requestDeadline)}</b>
              </Text>
            )}
            <Flex align="center" gap="2" color="smText">
              <Icon as={FiUsers} />
              <Text fontSize="sm">{t("{{count}} requests received", { count: data.count })}</Text>
            </Flex>
            <Text fontSize="xs" color="smMuted" maxW="md">
              {t(
                "Supplier identities are withheld until the deadline has passed, to ensure equal treatment.",
              )}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  const invitation = qualificationPublicCtx?.qualificationRound?.invitation ?? null;

  // Post-invitation: locked outcome record + table with outcomes
  if (invitation != null) {
    return (
      <Stack spacing="5">
        {requests.length > 0 && (
          <RequestTable event={event} requests={requests} />
        )}
        <InvitationOutcomeRecord invitation={invitation} requests={requests} citesFoa={citesFoa} />
      </Stack>
    );
  }

  // Zero requests post-deadline
  if (requests.length === 0) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10" textAlign="center">
          <Text color="smMuted">{t("No requests to participate were received.")}</Text>
        </CardBody>
      </Card>
    );
  }

  // Active flow: review → (select) → send

  const steps = deriveQualificationSteps({
    totalSubmittedOrLater: requests.length,
    unresolvedCount: unresolved.length,
    qualifiedCount: qualified.length,
    capped,
    selectedCount: selection.selectedIds.length,
    selectionValid: selection.isValid,
    missingReasonCount: selection.missingReasonCount,
    overMax: selection.overMax,
    underFloor: selection.underFloor,
  });

  const reviewDone = unresolved.length === 0 && requests.length > 0;
  const zeroQualified = qualified.length === 0 && reviewDone;

  return (
    <Stack spacing="5">
      {/* No stepper when zero qualified: steps 2/3 would point at unmounted sections — the
          guidance line below carries the state. */}
      {capped && !zeroQualified && (
        <QualificationStepper
          steps={steps}
          sectionIds={{ review: SECTION_REVIEW, select: SECTION_SELECT, send: SECTION_SEND }}
          isLoading={isLoading}
        />
      )}

      {/* Step 1: Request table */}
      <Box id={SECTION_REVIEW}>
        {/* tabIndex={-1}: focus target for the stepper's scroll-to-section */}
        <Heading as="h3" size="sm" color="smText" mb="2" tabIndex={-1}>
          {unresolved.length > 0
            ? t("{{resolved}} of {{total}} requests reviewed", {
                resolved: requests.length - unresolved.length,
                total: requests.length,
              })
            : capped
              ? t("{{count}} of {{total}} qualified will be invited to tender", {
                  count: selection.selectedIds.length,
                  total: qualified.length,
                })
              : t("{{count}} qualified — all will be invited to tender", {
                  count: qualified.length,
                })}
        </Heading>
        <RequestTable event={event} requests={requests} />
      </Box>

      {/* Step 2 (capped only): selection panel — muted when review not done */}
      {capped && !zeroQualified && (
        <Box id={SECTION_SELECT}>
          {!reviewDone ? (
            // Muted state: plain list without interactive checkboxes
            <Box
              bg="smBackgroundSecondary"
              border="1px solid"
              borderColor="smBorder"
              rounded="xl"
              p="4"
            >
              <Flex align="center" gap="2" mb="2">
                {/* tabIndex={-1}: focus target for the stepper's scroll-to-section */}
                <Heading as="h3" size="sm" color="smText" flex="1" tabIndex={-1}>
                  {t("Choose who to invite")}
                </Heading>
                <Badge colorScheme="gray" fontSize="xs">
                  {t("Waiting")}
                </Badge>
              </Flex>
              <Text fontSize="sm" color="smMuted" mb="3">
                {t("Activates when every request has been reviewed — {{count}} remaining.", {
                  count: unresolved.length,
                })}
              </Text>
              {qualified.length > 0 && (
                <>
                  <Text fontSize="xs" color="smMuted" mb="1">
                    {t("Qualified so far")}
                  </Text>
                  <UnorderedList spacing="0.5">
                    {qualified.map((r) => (
                      <ListItem key={r.id} fontSize="sm" color="smText">
                        {r.organizationName ?? r.organizationId}
                        {r.organizationNumber && (
                          <Text as="span" fontSize="xs" color="smMuted" ml="2" fontFamily="mono">
                            {r.organizationNumber}
                          </Text>
                        )}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </>
              )}
            </Box>
          ) : (
            <SelectionPanel qualified={qualified} limits={limits!} selection={selection} citesFoa={citesFoa} />
          )}
        </Box>
      )}

      {/* Step 3: send card (or zero-qualified guidance) */}
      {zeroQualified ? (
        <Text fontSize="sm" color="smMuted">
          {t("No qualified suppliers — consider cancelling the competition.")}
        </Text>
      ) : (
        <Box id={SECTION_SEND}>
          <SendInvitationCard event={event} requests={requests} selection={selection} limits={limits} />
        </Box>
      )}
    </Stack>
  );
};

// ---- Shared table component -----------------------------------------------

const RequestTable = ({
  event,
  requests,
}: {
  event: SourcingEventV2Dto;
  requests: BuyerParticipationRequestDto[];
}) => {
  const { t } = useTranslation();
  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
      <Table size="md">
        <Thead>
          <Tr>
            <Th>{t("Supplier")}</Th>
            <Th>{t("Submitted")}</Th>
            <Th>{t("Requirements reviewed")}</Th>
            <Th>{t("Status")}</Th>
            <Th aria-hidden />
          </Tr>
        </Thead>
        <Tbody>
          {requests.map((request) => (
            <RequestRow key={request.id} event={event} request={request} />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const RequestRow = ({
  event,
  request,
}: {
  event: SourcingEventV2Dto;
  request: BuyerParticipationRequestDto;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDateWithTime = useDisplayDateWithTime();
  // Dynamic key for langen: t('Not selected')
  const notSelected = request.selectionOutcome != null && !request.selectionOutcome.selected;
  const badge = notSelected
    ? { colorScheme: "orange", labelKey: "Not selected" }
    : participationStatusBadge(request.status);
  const progress = getRequestQualificationProgress(
    event.eventData.selectionCriteria ?? [],
    request.qualificationResponses ?? [],
  );

  // Dynamic keys for langen: t('Review changed')
  const reviewRetracted =
    request.qualificationVerdict?.qualified === true &&
    progress.pending > 0 &&
    request.selectionOutcome == null;

  return (
    <Tr
      cursor="pointer"
      _hover={{ bg: "smBackgroundSecondary" }}
      onClick={() => navigate(urls.v2.sourcingEvents.participationRequestDetail.go(event.id, request.id))}
    >
      <Td>
        <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
          {request.organizationName ?? request.organizationId}
        </Text>
        {request.organizationNumber && (
          <Text fontSize="xs" color="smMuted" fontFamily="mono">
            {request.organizationNumber}
          </Text>
        )}
      </Td>
      <Td fontSize="sm" color="smMuted" whiteSpace="nowrap">
        {request.submittedAt ? displayDateWithTime(request.submittedAt) : "–"}
      </Td>
      <Td fontSize="sm" color={progress.pending === 0 ? "smText" : "smMuted"} fontFamily="mono">
        {progress.hasRequirements ? `${progress.reviewed}/${progress.total}` : "–"}
      </Td>
      <Td>
        <HStack spacing="2" flexWrap="wrap">
          <Badge colorScheme={badge.colorScheme} fontSize="xs">
            {t(badge.labelKey)}
          </Badge>
          {reviewRetracted && (
            <Badge colorScheme="orange" fontSize="xs">
              {t("Review changed")}
            </Badge>
          )}
        </HStack>
      </Td>
      <Td textAlign="right">
        <Icon as={FiChevronRight} color="smMuted" />
      </Td>
    </Tr>
  );
};
